import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import styles from './FilterSelect.module.scss';

interface FilterSelectOption {
  value: string;
  label: string;
  icon?: string;
  color?: string;
}

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterSelectOption[];
  placeholder?: string;
  label?: string;
  className?: string;
  labelClassName?: string;
}

export const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  label,
  labelClassName,
  className,
}: FilterSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className="flex gap-3 items-center">
      {label && (
        <label className={classNames('font-base text-sm text-neutral-50', labelClassName)}>
          {label}:
        </label>
      )}
      <div className="relative w-full" ref={selectRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            'px-3 py-2 border border-neutral-20 rounded-lg text-sm bg-transparent font-base font-semibold flex items-center gap-2 min-w-[150px]',
            className,
          )}
        >
          {selectedOption?.icon && (
            <img src={selectedOption.icon} alt={selectedOption.label} className="w-4 h-4" />
          )}
          <span>{selectedOption?.label || placeholder}</span>
          <svg
            className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white-100 border border-neutral-20 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 font-medium ${
                  value === option.value ? 'bg-blue-50' : ''
                }`}
              >
                {option.icon && <img src={option.icon} alt={option.label} className="w-4 h-4" />}
                <span
                  className={classNames(
                    styles[option.color as keyof typeof styles],
                    'font-semibold',
                  )}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
